<template>
  <div class="w1200 equity-page">
    <div class="page-header">
      <div class="page-title">自动产生股权与分红清单</div>
      <el-button type="primary" plain @click="goBack">返回大厅</el-button>
    </div>

    <div v-loading="loading" class="page-body">
      <template v-if="equityData">
        <!-- 规则说明区 -->
        <div class="rule-card">
          <div class="rule-row">
            <span class="rule-label">一、众筹招股基本说明：</span>
          </div>
          <div class="rule-row indent">
            本众筹由 <b>{{ equityData.bettingShopAddress || '-' }}</b>（站号：{{ equityData.bettingShopNumber || '-' }}）发起，
            关联实战方案 <b>{{ equityData.schemeName || '未关联' }}</b>。
          </div>
          <div class="rule-row indent">
            众筹总额：<b>{{ money(equityData.crowdfundingAmount) }}</b> 元；
            总股数：<b>{{ equityData.totalShareNumber || 0 }}</b> 股；
            每股价格：<b>{{ money(equityData.crowdfundingPrice) }}</b> 缩币；
            已认购：<b>{{ equityData.subscribedShareNumber || 0 }}</b> 股；
            剩余：<b>{{ equityData.remainingShareNumber || 0 }}</b> 股。
          </div>
          <div class="rule-row indent">
            税后中奖总额：<b>{{ money(equityData.afterTaxAmount) }}</b> 元；
            实战票盈亏总额：<b :class="profitClassOf(equityData.profitLossAmount)">{{ money(equityData.profitLossAmount) }}</b> 元；
            每股分红：<b>{{ money(equityData.distributeBonus) }}</b> 缩币；
            应分红总额：<b>{{ money(equityData.totalPayableAmount) }}</b> 缩币。
          </div>

          <div class="rule-row" style="margin-top: 12px;">
            <span class="rule-label">二、众筹分红清单：</span>
          </div>
          <div class="rule-row indent">
            按照平台规则，由实战方案的人或团队认购并参与实战的众筹，
            开奖后依据每股分红自动生成本清单；分红状态以投注站实际派奖凭证为准。
          </div>
        </div>

        <!-- 分红清单表格 -->
        <div class="table-wrap">
          <el-table :data="equityData.purchaserList || []" empty-text="暂无认购记录"
            :header-cell-style="{ 'textAlign': 'center' }" :cell-style="{ 'textAlign': 'center' }" border>
            <el-table-column type="index" label="序号" width="60"></el-table-column>
            <el-table-column prop="purchaser" label="参与用户名" show-overflow-tooltip min-width="140"></el-table-column>
            <el-table-column prop="purchaserNumber" label="占股份数" width="100"></el-table-column>
            <el-table-column label="消费缩币" width="120">
              <template slot-scope="{ row }">{{ money(row.consumeCoin) }}</template>
            </el-table-column>
            <el-table-column label="占股比例" width="100">
              <template slot-scope="{ row }">{{ row.shareRatio != null ? row.shareRatio + '%' : '-' }}</template>
            </el-table-column>
            <el-table-column label="分红" width="120">
              <template slot-scope="{ row }">{{ money(row.dividendAmount) }} 缩币</template>
            </el-table-column>
            <el-table-column label="是否兑现" width="120">
              <template slot-scope="{ row }">
                <el-tag :type="bonusTagType(row.bonusStatus)" size="small">{{ row.bonusStatusIntro || '-' }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="分红二维码" width="100">
              <template slot-scope="{ row }">
                <template v-if="row.bonusStatus == '1' && dividendQrUrl">
                  <img :src="dividendQrUrl" class="qr-thumb" alt="分红二维码" @click="qrPreviewVisible = true" />
                </template>
                <template v-else-if="row.bonusStatus == '2'">-</template>
                <template v-else>-</template>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 计算公式 -->
        <div class="formula-wrap" v-if="equityData.profitLossFormula || equityData.dividendFormula">
          <div v-if="equityData.profitLossFormula" class="formula-item">
            <span class="formula-label">盈亏计算：</span>{{ equityData.profitLossFormula }}
          </div>
          <div v-if="equityData.dividendFormula" class="formula-item">
            <span class="formula-label">分红计算：</span>{{ equityData.dividendFormula }}
          </div>
        </div>
      </template>

      <div v-else-if="!loading" class="tip-msg">未获取到股权清单，请返回大厅重试。</div>
    </div>

    <!-- 分红二维码大图预览 -->
    <el-dialog title="分红二维码" :visible.sync="qrPreviewVisible" width="340px">
      <div class="qr-preview-box" v-if="dividendQrUrl">
        <img :src="dividendQrUrl" class="qr-big" alt="分红二维码" />
        <div class="qr-tip">微信/浏览器扫码查看分红明细页</div>
      </div>
      <div v-else-if="dividendQrLoading" class="tip-msg">生成中…</div>
      <div v-else class="tip-msg">暂未生成分红二维码</div>
    </el-dialog>
  </div>
</template>

<script>
import { bettingShopEquityList, bettingShopShareDividendQrcode } from "@/api/apply";

export default {
  name: "EquityDetail",
  data() {
    return {
      crowdfundingId: null,
      loading: false,
      equityData: null,
      dividendQrUrl: "",
      dividendQrLoading: false,
      qrPreviewVisible: false,
    };
  },
  mounted() {
    this.crowdfundingId = this.$route.params.crowdfundingId;
    if (!this.crowdfundingId) {
      this.$message.warning("缺少众筹招股 ID");
      return;
    }
    this.loadEquity();
    this.loadDividendQr();
  },
  methods: {
    goBack() {
      this.$router.push("/sttzzbfzx/zhaogu");
    },
    loadEquity() {
      this.loading = true;
      bettingShopEquityList(this.crowdfundingId)
        .then((res) => {
          const data = res && res.data !== undefined ? res.data : res;
          this.equityData = data || null;
          this.loading = false;
        })
        .catch(() => {
          this.equityData = null;
          this.loading = false;
          this.$message.error("股权清单加载失败");
        });
    },
    loadDividendQr() {
      this.dividendQrLoading = true;
      bettingShopShareDividendQrcode(this.crowdfundingId)
        .then((res) => {
          const data = res && res.data !== undefined ? res.data : res;
          this.dividendQrUrl = typeof data === "string" && data ? data : "";
          this.dividendQrLoading = false;
        })
        .catch(() => {
          this.dividendQrUrl = "";
          this.dividendQrLoading = false;
        });
    },
    money(v) {
      if (v == null || v === "") return "-";
      const n = Number(v);
      return isNaN(n) ? v : n;
    },
    profitClassOf(v) {
      const n = Number(v || 0);
      return n >= 0 ? "profit-up" : "profit-down";
    },
    bonusTagType(status) {
      if (status === "1") return "success";
      if (status === "2") return "info";
      return "warning";
    },
  },
};
</script>

<style lang="scss" scoped>
.equity-page {
  background: #ffffff;
  padding: 20px;
  margin-top: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border-radius: 8px;

  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 2px solid #347caf;
    padding-bottom: 16px;
    margin-bottom: 20px;

    .page-title {
      font-size: 22px;
      font-weight: bold;
      color: #347caf;
    }
  }

  .page-body {
    min-height: 200px;
  }

  .rule-card {
    border: 1px solid #e4e7ed;
    border-radius: 10px;
    background: linear-gradient(135deg, #f5faff 0%, #eaf2fa 100%);
    padding: 18px 22px;
    margin-bottom: 20px;
    font-size: 14px;
    color: #333;
    line-height: 1.8;

    .rule-row {
      &.indent {
        padding-left: 18px;
      }

      b {
        color: #347caf;
      }
    }

    .rule-label {
      color: #347caf;
      font-weight: bold;
    }
  }

  .table-wrap {
    margin-bottom: 20px;
  }

  .formula-wrap {
    border: 1px dashed #c0c4cc;
    border-radius: 8px;
    background: #f5f7fa;
    padding: 14px 18px;
    margin-top: 10px;

    .formula-item {
      font-size: 13px;
      color: #606266;
      line-height: 1.8;

      .formula-label {
        color: #347caf;
        font-weight: bold;
      }
    }
  }

  .tip-msg {
    color: #898af7;
    font-weight: bold;
    padding: 40px 0;
    text-align: center;
  }

  .qr-thumb {
    width: 44px;
    height: 44px;
    object-fit: contain;
    cursor: pointer;
    vertical-align: middle;
    border: 1px solid #e4e7ed;
    border-radius: 4px;
    padding: 2px;

    &:hover {
      box-shadow: 0 2px 8px rgba(52, 124, 175, 0.3);
    }
  }
}

.profit-up {
  color: #f56c6c;
}

.profit-down {
  color: #67c23a;
}

.qr-preview-box {
  text-align: center;

  .qr-big {
    width: 240px;
    height: 240px;
    object-fit: contain;
  }

  .qr-tip {
    margin-top: 12px;
    font-size: 13px;
    color: #888;
  }
}
</style>
